import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { stageAging, STAGE_KEYS, deriveOrderFlags } from '../utils/wipOrderLogic';
import { SkelStats, SkelCardGrid } from './Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PERIODS = [
  { k: 'week', label: 'This week', days: 7 },
  { k: 'month', label: 'This month', days: 30 },
  { k: 'quarter', label: 'Last 90 days', days: 90 },
  { k: 'all', label: 'All time', days: 0 },
];
const pct = (n, d) => d ? Math.round((n / d) * 100) : 0;

// The four aging reports: ideal vs actual for each pipeline stage.
const REPORTS = [
  { stage: 'issuance', title: 'Issuance Aging', sub: 'Ideal Issuance vs Actual Issuance' },
  { stage: 'receiving', title: 'Receiving Aging', sub: 'Ideal Receiving vs Actual Receiving' },
  { stage: 'dispatch', title: 'Dispatch Aging', sub: 'Ideal Dispatch vs Actual Dispatch' },
  { stage: 'delivery', title: 'Delivery Aging', sub: 'Ideal Delivery vs Actual Delivery' },
];

// Aggregate aging for one stage across a set of rows.
function agingFor(rows, stage) {
  const { ideal, actual } = STAGE_KEYS[stage];
  let onTime = 0, late = 0, pending = 0, overduePending = 0, sumLate = 0, worst = 0;
  for (const r of rows) {
    const a = stageAging(r.fields || {}, ideal, actual);
    if (a.days == null) continue;                 // no ideal date to measure against
    if (a.done) {
      if (a.delayed) { late++; sumLate += a.days; if (a.days > worst) worst = a.days; }
      else onTime++;
    } else {
      pending++;
      if (a.delayed) overduePending++;
    }
  }
  const measured = onTime + late;
  const total = measured + pending;
  return { onTime, late, pending, overduePending, total, avgLate: late ? Math.round(sumLate / late) : 0, onTimePct: pct(onTime, measured), latePct: pct(late, measured), worst };
}

export default function OrderAnalytics({ workspaceUuid, shopDomain, brandName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [period, setPeriod] = useState('month');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = shopDomain ? `&shopDomain=${encodeURIComponent(shopDomain)}` : '';
      const r = await axios.get(`${API_URL}/api/wip/orders?workspaceUuid=${workspaceUuid}&limit=1000${qs}`, auth());
      setRows(r.data.items || []);
    } catch (e) { setErr(e.response?.data?.message || 'Could not load analytics.'); }
    finally { setLoading(false); }
  }, [workspaceUuid, shopDomain]);
  useEffect(() => { load(); }, [load]);

  const scoped = useMemo(() => {
    const days = PERIODS.find(p => p.k === period)?.days || 0;
    const cutoff = days ? Date.now() - days * 86400000 : 0;
    return rows.filter(r => {
      if (!cutoff) return true;
      const d = new Date(r.fields?.orderDate).getTime();
      return isNaN(d) ? true : d >= cutoff;
    });
  }, [rows, period]);

  const reports = useMemo(() => REPORTS.map(r => ({ ...r, ...agingFor(scoped, r.stage) })), [scoped]);
  const summary = useMemo(() => {
    let delivered = 0, fulfilled = 0, returns = 0;
    for (const r of scoped) {
      const fl = deriveOrderFlags(r.fields || {});
      if (fl.delivered) delivered++;
      if (fl.fulfilled) fulfilled++;
      if (/refund|return|rto/i.test(`${r.fields?.refundStatus || ''} ${r.fields?.deliveryStatus || ''}`)) returns++;
    }
    return { total: scoped.length, delivered, fulfilled, returns };
  }, [scoped]);

  return (
    <div className="oa-wrap">
      <style>{css}</style>
      <div className="oa-head">
        <div>
          <h2 className="oa-title">Order Analytics{brandName ? ` — ${brandName}` : ''}</h2>
          <p className="oa-sub">Aging reports — how actual timelines compare to your ideal timelines at each stage.</p>
        </div>
        <div className="oa-periods">
          {PERIODS.map(p => <button key={p.k} className={`oa-per ${period === p.k ? 'on' : ''}`} onClick={() => setPeriod(p.k)}>{p.label}</button>)}
        </div>
      </div>

      {err && <div className="oa-err">{err}</div>}
      {loading ? (
        <>
          <SkelStats count={4} />
          <SkelCardGrid count={4} minWidth={340} lines={4} />
        </>
      ) : (
        <>
          <div className="oa-cards">
            <div className="oa-card"><span>Orders</span><b>{summary.total}</b></div>
            <div className="oa-card oa-ok"><span>Fulfilled</span><b>{summary.fulfilled}</b><i>{pct(summary.fulfilled, summary.total)}%</i></div>
            <div className="oa-card oa-ok"><span>Delivered</span><b>{summary.delivered}</b><i>{pct(summary.delivered, summary.total)}%</i></div>
            <div className="oa-card oa-bad"><span>Returns / refunds</span><b>{summary.returns}</b><i>{pct(summary.returns, summary.total)}%</i></div>
          </div>

          <div className="oa-reports">
            {reports.map(rep => {
              const measured = rep.onTime + rep.late;
              return (
                <div className="oa-report" key={rep.stage}>
                  <div className="oa-report-head">
                    <div><h3>{rep.title}</h3><p>{rep.sub}</p></div>
                    <div className={`oa-rate ${rep.onTimePct >= 80 ? 'good' : rep.onTimePct >= 50 ? 'mid' : 'bad'}`}>{measured ? `${rep.onTimePct}%` : '—'}<span>on time</span></div>
                  </div>
                  {/* on-time vs late bar */}
                  <div className="oa-bar2" title={`${rep.onTime} on time · ${rep.late} late`}>
                    <div className="oa-bar2-ok" style={{ width: `${measured ? rep.onTimePct : 0}%` }} />
                    <div className="oa-bar2-bad" style={{ width: `${measured ? rep.latePct : 0}%` }} />
                  </div>
                  <div className="oa-metrics">
                    <div className="oa-metric oa-m-ok"><b>{rep.onTime}</b><span>On time</span></div>
                    <div className="oa-metric oa-m-bad"><b>{rep.late}</b><span>Late</span></div>
                    <div className="oa-metric"><b>{rep.pending}</b><span>Pending{rep.overduePending ? ` (${rep.overduePending} overdue)` : ''}</span></div>
                    <div className="oa-metric oa-m-bad"><b>{rep.avgLate ? `${rep.avgLate}d` : '0d'}</b><span>Avg delay</span></div>
                    <div className="oa-metric"><b>{rep.worst ? `${rep.worst}d` : '—'}</b><span>Worst</span></div>
                  </div>
                  {measured === 0 && rep.pending === 0 && <div className="oa-none">No dated orders in this period.</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const css = `
.oa-wrap{ font-size:13px; }
.oa-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:14px; flex-wrap:wrap; margin-bottom:16px; }
.oa-title{ margin:0; font-size:18px; font-weight:700; letter-spacing:-0.03em; color:#111111; }
.oa-sub{ margin:3px 0 0; color:#6B6862; font-size:12.5px; }
.oa-periods{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.oa-per{ background:#fff; border:1px solid #E7E1D6; cursor:pointer; font-size:12.5px; font-weight:500; color:#3A3A37; padding:8px 15px; border-radius:999px; transition:color .15s, background .15s, border-color .15s; }
.oa-per:hover{ color:#111111; background:#FAF7F1; }
.oa-per.on{ background:#111111; color:#F9F4EE; border-color:#111111; }
.oa-err{ font-size:13px; color:#8A2B16; background:#FBEDE9; border:1px solid #E7E1D6; padding:10px 14px; border-radius:14px; margin-bottom:14px; font-weight:600; }
.oa-loading{ display:flex; justify-content:center; padding:50px; }
.oa-spin{ width:26px; height:26px; border:3px solid #E7E1D6; border-top-color:#111111; border-radius:50%; animation:oaSpin .7s linear infinite; }
@keyframes oaSpin{ to{ transform:rotate(360deg); } }

.oa-cards{ display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px; margin-bottom:16px; }
.oa-card{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; padding:14px 16px; }
.oa-card span{ font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; }
.oa-card b{ display:block; font-size:24px; font-weight:700; letter-spacing:-0.03em; color:#111111; line-height:1.2; margin-top:2px; font-family:'IBM Plex Mono',monospace; }
.oa-card i{ font-style:normal; font-size:11.5px; color:#9C978C; font-weight:600; font-family:'IBM Plex Mono',monospace; }
.oa-ok b{ color:#0F6E56; } .oa-bad b{ color:#B23A3A; }

.oa-reports{ display:grid; grid-template-columns:repeat(auto-fit,minmax(340px,1fr)); gap:14px; }
.oa-report{ background:#FFFFFF; border:1px solid #E7E1D6; border-radius:14px; padding:16px 18px; }
.oa-report-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:12px; }
.oa-report-head h3{ margin:0; font-size:14px; font-weight:700; letter-spacing:-0.03em; color:#111111; }
.oa-report-head p{ margin:2px 0 0; font-size:11.5px; color:#9C978C; }
.oa-rate{ font-size:22px; font-weight:700; letter-spacing:-0.03em; text-align:right; line-height:1; white-space:nowrap; font-family:'IBM Plex Mono',monospace; }
.oa-rate span{ display:block; font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#9C978C; margin-top:2px; font-family:inherit; }
.oa-rate.good{ color:#0F6E56; } .oa-rate.mid{ color:#8A5A12; } .oa-rate.bad{ color:#B23A3A; }
.oa-bar2{ display:flex; height:12px; border-radius:6px; overflow:hidden; background:#FAF7F1; margin-bottom:14px; }
.oa-bar2-ok{ background:#0F6E56; } .oa-bar2-bad{ background:#B23A3A; }
.oa-metrics{ display:grid; grid-template-columns:repeat(5,1fr); gap:6px; }
.oa-metric{ text-align:center; background:#FAF7F1; border:1px solid #E7E1D6; border-radius:9px; padding:7px 4px; }
.oa-metric b{ display:block; font-size:16px; font-weight:700; color:#111111; font-family:'IBM Plex Mono',monospace; }
.oa-metric span{ font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.02em; color:#9C978C; }
.oa-m-ok b{ color:#0F6E56; } .oa-m-bad b{ color:#B23A3A; }
.oa-none{ color:#9C978C; font-weight:600; padding:10px 0 0; font-size:12px; }
`;
